import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, PenLine, Film, Clapperboard, Headphones } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import EntryCard from "@/components/EntryCard";
import ContentCard from "@/components/ContentCard";
import ProductSection from "@/components/ProductSection";
import barbara from "@/assets/barbara.jpg";
import barbaraAbout from "@/assets/barbara_about.jpg";
import entryWriting from "@/assets/entry_writing.jpg";
import entryVideo from "@/assets/entry_video.jpg";
import entryShort from "@/assets/entry_short.jpg";
import entryAudio from "@/assets/entry_audio.jpg";

const STATEMENT = "Non sono qui per dirti cosa pensare. Sono qui per mostrarti come vedo le cose.";

function highlightUnfiltered(text) {
  if (!text) return text;
  const parts = text.split(/(unfiltered)/gi);
  return parts.map((part, i) =>
    part.toLowerCase() === "unfiltered"
      ? <span key={i} className="text-[#C6899E] font-medium tracking-wide">UNFILTERED</span>
      : part
  );
}

export default function Home() {
  const { t, lang } = useLang();
  const [settings, setSettings] = useState(null);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/content", { params: { featured: true, lang } }).then((r) => setFeatured(r.data)).catch(() => {});
  }, [lang]);

  const heroImg = settings?.hero_image_path ? mediaUrl(settings.hero_image_path) : barbara;
  const aboutImg = settings?.about_image_path ? mediaUrl(settings.about_image_path) : barbaraAbout;
  const intro = settings?.intro_text?.[lang] || settings?.intro_text?.it || "";

  const entries = [
    { to: "/writing", label: t("nav_writing"), desc: t("writing_desc"), action: t("read"), icon: PenLine, image: entryWriting },
    { to: "/video", label: t("nav_video"), desc: t("video_desc"), action: t("watch"), icon: Film, image: entryVideo },
    { to: "/short", label: t("nav_short"), desc: t("short_desc"), action: t("watch"), icon: Clapperboard, image: entryShort },
    { to: "/audio", label: t("nav_audio"), desc: t("audio_desc"), action: t("listen"), icon: Headphones, image: entryAudio },
  ];

  const typeToPath = { writing: "/writing", video: "/video", short: "/short", audio: "/audio" };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16 pt-10 sm:pt-16 pb-16 sm:pb-24">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="order-2 lg:order-1"
          >
            <div className="eyebrow mb-5">by Barbara Uwadie</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[0.95]">
              UNFILTERED
            </h1>
            <div className="gold-line my-7" />
            <p className="font-serif italic text-2xl sm:text-3xl lg:text-[2.1rem] leading-snug text-[#3D3238] max-w-xl">
              “{STATEMENT}”
            </p>
            <p className="mt-7 text-[#8C7E83] text-base sm:text-lg leading-relaxed max-w-lg">{highlightUnfiltered(intro)}</p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/su-di-me" data-testid="hero-about-cta"
                className="group inline-flex items-center gap-2 bg-[#C6899E] text-[#4A2233] rounded-full px-7 py-3.5 text-sm uppercase tracking-[0.15em] hover:bg-[#B4718C] transition-colors">
                {t("nav_about")}
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <Link to="/writing" data-testid="hero-explore-cta"
                className="link-underline text-sm uppercase tracking-[0.15em] text-[#3D3238]">
                {t("explore")}
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="order-1 lg:order-2"
          >
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-[rgba(198,137,158,0.2)]">
                <img src={heroImg} alt="Barbara Uwadie" className="w-full h-full object-cover" data-testid="hero-portrait" />
              </div>
              <div className="absolute -bottom-4 -left-4 hidden sm:block bg-[#FBF5F1] border border-[rgba(198,137,158,0.3)] rounded-full px-5 py-3">
                <span className="eyebrow">{t("hero_intro")}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ENTRY POINTS */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16 pb-20 sm:pb-28">
        <div className="mb-10 sm:mb-14">
          <div className="eyebrow mb-3">{t("explore")}</div>
          <h2 className="font-serif italic text-4xl sm:text-5xl font-normal tracking-tight text-[#3D3238]">
            {lang === "it" ? "Entra nel mio mondo" : lang === "en" ? "Enter my world" : lang === "fr" ? "Entrez dans mon monde" : "Entra en mi mundo"}
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
          {entries.map((e, i) => (
            <EntryCard key={e.to} {...e} index={i} />
          ))}
        </div>
      </section>

      {/* SU DI ME TEASER */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16 pb-20 sm:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}
          className="rounded-3xl bg-[#ECD8DE] text-[#4A2233] overflow-hidden grid md:grid-cols-[1.2fr_1fr]"
        >
          <div className="p-8 sm:p-14 flex flex-col justify-center">
            <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#A76B86] mb-4">Su di me</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight">
              {lang === "it" ? "Un modo un po' diverso di guardare le cose." : lang === "en" ? "A slightly different way of seeing things." : lang === "fr" ? "Une façon un peu différente de voir les choses." : "Una forma un poco distinta de ver las cosas."}
            </h2>
            <p className="mt-6 text-[#6B5860] leading-relaxed max-w-md">
              {(settings?.about_text?.[lang] || settings?.about_text?.it || "").split("\n\n")[0]}
            </p>
            <Link to="/su-di-me" data-testid="about-teaser-cta"
              className="group mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-[#4A2233] border-b border-[#4A2233] pb-1 w-fit hover:text-[#A76B86] hover:border-[#A76B86] transition-colors">
              {t("discover_about")}
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>
          <div className="min-h-[280px] md:min-h-full">
            <img src={aboutImg} alt="Barbara" className="w-full h-full object-cover" />
          </div>
        </motion.div>
      </section>

      {/* MAIN PRODUCT */}
      <ProductSection />

      {/* FEATURED */}
      {featured.length > 0 && (        <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16 pb-24 sm:pb-32">
          <div className="mb-10 flex items-end justify-between">
            <div>
              <div className="eyebrow mb-3">{t("featured")}</div>
              <h2 className="font-serif text-3xl sm:text-4xl font-medium tracking-tight">
                {lang === "it" ? "Selezione dal mio mondo" : lang === "en" ? "A selection from my world" : lang === "fr" ? "Une sélection de mon monde" : "Una selección de mi mundo"}
              </h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.slice(0, 4).map((item, i) => (
              <ContentCard key={item.id} item={item} index={i}
                to={item.type === "writing" ? `/writing/${item.slug}` : typeToPath[item.type]}
                vertical={item.type === "short"} showPlay={["video", "short", "audio"].includes(item.type)} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
